import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { PopularLazyLoadSelector } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineComponent from 'webapp/components/filter/line';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';
import WizardSourcesGridItemComponent from 'webapp/components/wizard/sources/grid-item';

import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortAvailableSourceInWizard from 'webapp/models-custom/filter-sort/available-source-in-wizard';
import type SourceType from 'webapp/models-custom/source-type';

export type TrackingOriginIntegrationsSelectionPopup = 'home' | 'datamanager';

export default class AccountPopupIntegrationsSelectionComponent extends PopupInnerModalComponent<PopupData.Account.IntegrationSelection> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('flow/integrations-selection-popup')
	declare flowIntegrationsSelectionPopupService: Service.Flow.IntegrationsSelectionPopup;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('request') declare requestService: Service.Request;

	@tracked isRightSideLoaderShown = true;

	visibleCheckContainerNode: HTMLElement | null = null;
	filterSort: FilterSortAvailableSourceInWizard;
	batchSelect: BatchSelect<SourceType>;

	popupTitle = 'Let us know which <strong>tools</strong> are you using';
	popupDescription =
		"Select software you use and we'll help you connect them later, so you can enjoy a personalized Databox experience tailored to your needs and preferences.";

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const allItems = this.mmSourceTypesService.sourceTypeModels.filter(
			(item) => !item.isHidden && !item.showInGroupOnly,
		);

		this.batchSelect = new BatchSelect<SourceType>({
			getAllItems: () => [],
			getOptions: () => [],
			doBatchAction: () => Promise.resolve(),
		});

		this.filterSort = new FilterSortAvailableSourceInWizard({
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return allItems;
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}
			},
		});

		this.analyticsService.event(TrackingEvent.SELECT_INTEGRATION_POPUP_SHOWN, {
			origin: this.popupData.origin,
		});
		this.flowIntegrationsSelectionPopupService.wasPopupShown = true;

		this.isRightSideLoaderShown = false;
	}

	get saveButtonText(): string {
		const selectedCount = this.batchSelect.selectedItems.length;

		return 'Save selection ' + (selectedCount > 0 ? `(${selectedCount})` : '');
	}

	@action
	save(): void {
		const sourcesSelection = this.batchSelect.selectedItems.map((item) => item.key);

		this.submitInProgress = true;

		this.requestService
			.api<APIData.Account.UpdateSourcesOpportunityResponse>(
				'POST',
				'/account/update-sources-selection',
				{ sourcesSelection },
			)
			.then((response) => {
				this.accountService.updateMetadata(response);
				this.analyticsService.event(TrackingEvent.SELECT_INTEGRATION_POPUP_COMPLETED, {
					origin: this.popupData.origin,
				});
				this.infolayerService.ok('Your selection has been successfully saved.');

				void this.currentUserService
					.getMetadata()
					.then((response: APIData.User.MetadataResponse) => {
						this.currentUserService.updateMetadata(response);
					});
			})
			.finally(() => {
				this.submitInProgress = false;
				this.close();
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;
	}
	<template>
		<div class="content">
			<div class="sides-ct has-wide-left-side">
				<div class="side side-left">
					<img src="/images/svg/integrations.svg" width="auto" height="60" alt />
					<div class="ct">
						<h2>{{{this.popupTitle}}}</h2>
						<p>{{this.popupDescription}}</p>
					</div>
				</div>
				<div
					class="side side-right {{if this.isRightSideLoaderShown 'side-right--loading'}}"
				>

					<h2>{{{this.popupTitle}}}</h2>
					{{! shown only on mobile when side-left hidden }}
					<p>{{this.popupDescription}}</p>
					{{! shown only on mobile when side-left hidden }}

					<FilterLineComponent @model={{this.filterSort}} />

					<div class="items-list items--scrollable">
						{{#if this.filterSort.noResults}}
							<FilterNoResultsComponent @model={{this.filterSort}} />
						{{/if}}

						<CommonListLazyLoadComponent
							@classNames="lazy-grid"
							@filterSort={{this.filterSort}}
							@initialRowCount={{10}}
							@loadMoreRowCount={{5}}
							@animationProperty="id"
							@autoLoaded={{true}}
							@bottomDistance={{300}}
							@scrollableContainerSelector=".items--scrollable"
							as |visibleItems exporter|
						>
							<ul
								class="flex-ct flex-ct--size-source"
								data-hidden={{if this.filterSort.noResults "true" "false"}}
								{{didInsert this.setupView}}
								{{visibleCheckContainer}}
								{{didUpdate this.triggerContentChange visibleItems}}
							>
								{{#each visibleItems as |item|}}
									<WizardSourcesGridItemComponent
										@model={{item}}
										@batchSelect={{this.batchSelect}}
										@hideConnectedFlag={{true}}
									/>
								{{/each}}

								{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
								{{#each (makeArray length=exporter.itemsPerRow)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}
							</ul>
							{{#unless exporter.areAllItemsLoaded}}
								<CommonListLoaderComponent />
							{{/unless}}
						</CommonListLazyLoadComponent>

					</div>

					<div class="controls2">
						<div class="controls2__left">
							<div class="text">
								By saving the selection we will personalize your experience.
							</div>
						</div>
						<div class="controls2__right">
							<SubmitButtonComponent
								@text={{this.saveButtonText}}
								@size="big"
								@noDelay={{true}}
								@isDisabled={{eq this.batchSelect.selectedItems.length 0}}
								@submitInProgress={{this.submitInProgress}}
								@onTrigger={{this.save}}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</template>
}
